import React from 'react';
import styled from '@emotion/styled';
import { Droppable } from 'react-beautiful-dnd-short-press';
import { transparentize } from 'polished';
import { DraggableEntityThumbnail } from './draggable-entity-thumbnail';
import { TierViewEntityThumbnail } from './tierview-entity-thumbnail';
import { isFiltered } from '../../util/filter';
import { minBreakpoints } from 'gatsby-theme-apollo';

const defaultTiers = () => [
  {
    name: 'S',
    characters: []
  },
  {
    name: 'A',
    characters: []
  },
  {
    name: 'B',
    characters: []
  },
  {
    name: 'C',
    characters: []
  },
  {
    name: 'D',
    characters: []
  }
];

const StyledGrid = styled.div(props => ({
  display: `grid`,
  backgroundColor: `#222`,
  gridTemplateColumns: `minmax(min-content, 1fr) 18fr`,
  gridAutoRows: `minmax(min-content, max-content)`,
  flexShrink: 0,
  width: `100%`,
  minHeight: `50px`,
  borderRadius: `15px`,
  overflow: `hidden`,
  border: `1px solid ${props => props.theme.divider}`,
  marginBottom: `5px`,
}))

const TierGrid = ({ children }) => {
  return (
    <div style={{ width: '100%' }}>
      <StyledGrid>{children}</StyledGrid>
    </div>
  );
};

const GridHeaderRowCell = styled.div`
  grid-row-start: 1;
  grid-row-end: 2;
  background-color: #111;
`;

const GridHeaderRowNameColumn = styled(GridHeaderRowCell)({
  gridColumnStart: 1,
  gridColumnEnd: 2
});

const GridHeaderRowContentColumn = styled(GridHeaderRowCell)({
  gridColumnStart: 2,
  gridColumnEnd: 3
});

const TierHeader = styled.div(props => ({
  backgroundColor: props.bgColor,
  display: `flex`,
  justifyContent: `center`,
  alignItems: `center`,
  color: `black`,
  fontWeight: `bold`,
  padding: `5px`,
  minWidth: `60px`,
  maxWidth: `80px`,
  minHeight: `50px`,
  width: `100%`,
  gridColumnStart: `1`,
  gridColumnEnd: `2`,
  gridRowStart: props.rowStart,
  gridRowEnd: props.rowEnd,
  [minBreakpoints.sm]: {
    maxWidth: `100px`
  },
}));

const TierSettings = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: bold;
`;

const StyledTierRow = styled.div`
  display: flex;
  flex-shrink: 1;
  position: relative;
  flex-wrap: wrap;
  justify-content: flex-start;
  padding-left: ${props => (props.hide ? '0px' : '4px')};
  box-sizing: border-box;
  grid-column-start: 2;
  grid-column-end: 3;
  background-color: ${props => (props.selected ? 'orange' : 'inherit')};
  min-height: ${props => (props.hide ? '0px' : `${props.size.height + 15}px`)};
  border-bottom: ${props =>
    props.lastRow
      ? `1px solid ${props.color ? transparentize(0.6, props.color) : '#222'}`
      : 'none'};
  :hover {
    background-color: ${props => (props.selected ? `orange` : `#333`)};
  }
`;

class DroppableTierRow extends React.Component {
  render() {
    const {
      tierIndex,
      rowIndex,
      children,
      emptyDropPreviewRow,
      hide,
      lastRow,
      color,
      size,
      selected,
    } = this.props;

    return (
      <Droppable droppableId={`tier_${tierIndex}-row_${rowIndex}`} direction="horizontal">
        {provided => (
          <StyledTierRow
            ref={provided.innerRef}
            provided={provided}
            emptyDropPreviewRow={emptyDropPreviewRow}
            hide={hide}
            lastRow={lastRow}
            color={color}
            size={size}
            selected={selected}
          >
            {children}
            {provided.placeholder}
          </StyledTierRow>
        )}
      </Droppable>
    );
  }
}

const Tier = ({
  tierIndex,
  characters,
  characterSet,
  charactersPerRow,
  gameStats,
  gameName,
  entityTypePlural,
  rowsNeeded,
  highlights,
  currentlyDraggingDraggableId,
  disableDrag,
  searchText,
  filters,
  showDropPreviewRows,
  color,
  size,
  logEditorEvent,
  selected,
}) => {
  let rows = [];

  if (charactersPerRow !== 0) {
    rows = Array(rowsNeeded).fill(0);
  }

  return (
    <>
      {rows.map((row, rowIndex) => {
        const characterSlice = characters.slice(
          rowIndex * charactersPerRow,
          rowIndex * charactersPerRow + charactersPerRow
        );

        const lastRow = rowIndex === rows.length - 1;

        const emptyDropPreviewRow =
          rowIndex > 0 && lastRow && characters && characterSlice.length === 0;

        const hide = emptyDropPreviewRow && !showDropPreviewRows;
        return (
          <DroppableTierRow
            key={tierIndex+rowIndex}
            tierIndex={tierIndex}
            rowIndex={rowIndex}
            emptyDropPreviewRow={emptyDropPreviewRow}
            hide={hide}
            lastRow={lastRow}
            color={color}
            size={size}
            selected={selected}
          >
            {characters &&
              characterSlice.map((entity, index) => {
                let characterSetEntity = characterSet.find(char => char.id === entity.id);
                if (!characterSetEntity) {
                  characterSetEntity = entity;
                  console.log(`Failed to find entity ${entity.name} in character set`);
                }
                
                let { stats, name, id, thumbnailURI, entityType } = characterSetEntity;
                const minimalEntityFromSet = { name, id, thumbnailURI, entityType };

                return (
                  <DraggableEntityThumbnail
                    key={`${minimalEntityFromSet.id}-charthumb`}
                    index={index}
                    character={minimalEntityFromSet}
                    stats={stats}
                    gameStats={gameStats}
                    gameName={gameName}
                    entityTypePlural={entityTypePlural}
                    logEditorEvent={logEditorEvent}
                    currentlyDraggingDraggableId={currentlyDraggingDraggableId}
                    disableDrag={disableDrag}
                    highlights={highlights}
                    size={size}
                    activeFiltering={(filters && filters.length !== 0) || searchText !== ''}
                    isFilteredOut={isFiltered(minimalEntityFromSet, stats, searchText, filters)}
                  />
                );
              })}
          </DroppableTierRow>
        );
      })}
    </>
  );
};

const TierViewOnly = ({
  tierIndex,
  characters,
  characterSet,
  charactersPerRow,
  gameStats,
  gameName,
  entityTypePlural,
  rowsNeeded,
  highlights,
  currentlyDraggingDraggableId,
  disableDrag,
  searchText,
  filters,
  showDropPreviewRows,
  color,
  size,
  logEditorEvent,
  selected,
}) => {
  let rows = [];

  if (charactersPerRow !== 0) {
    rows = Array(rowsNeeded).fill(0);
  }

  return (
    <>
      {rows.map((row, rowIndex) => {
        const characterSlice = characters.slice(
          rowIndex * charactersPerRow,
          rowIndex * charactersPerRow + charactersPerRow
        );

        const lastRow = rowIndex === rows.length - 1;

        const emptyDropPreviewRow =
          rowIndex > 0 && lastRow && characters && characterSlice.length === 0;

        const hide = emptyDropPreviewRow && !showDropPreviewRows;
        return (
          <StyledTierRow
            hide={hide}
            lastRow={lastRow}
            color={color}
            size={size}
            selected={selected}
            key={tierIndex+rowIndex}
          >
            {characters &&
              characterSlice.map((entity, index) => {
                let characterSetEntity = characterSet.find(char => char.id === entity.id);
                if (!characterSetEntity) {
                  characterSetEntity = entity;
                  console.log(`Failed to find entity ${entity.name} in character set`);
                }
                
                let { stats, name, id, thumbnailURI, entityType, thumbnailSmall, thumbnailLarge } = characterSetEntity;
                const minimalEntityFromSet = { name, id, thumbnailURI, entityType, thumbnailSmall, thumbnailLarge };

                return (
                  <TierViewEntityThumbnail
                    key={`${minimalEntityFromSet.id}-charthumb`}
                    character={minimalEntityFromSet}
                    stats={stats}
                    gameStats={gameStats}
                    gameName={gameName}
                    entityTypePlural={entityTypePlural}
                    logEditorEvent={logEditorEvent}
                    highlights={highlights}
                    size={size}
                    activeFiltering={(filters && filters.length !== 0) || searchText !== ''}
                    isFilteredOut={isFiltered(minimalEntityFromSet, stats, searchText, filters)}
                  />
                );
              })}
          </StyledTierRow>
        );
      })}
    </>
  );
};

export { defaultTiers, TierGrid, Tier, TierViewOnly, TierSettings, TierHeader, GridHeaderRowNameColumn, GridHeaderRowContentColumn };
